import {
    useEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    ScrollView,
    Platform,
    View
} from "react-native";
import type {
    EditProfileModalProps
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextInput,
    Avatar,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    launchImageLibrary
} from "react-native-image-picker";
import {
    PencilIcon
} from "lucide-react-native";

const EditProfileModal = ({
    profilePicturePath,
    isVisible,
    username,
    address,
    onClose,
    onSave,
    email,
    phone
}: EditProfileModalProps) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        selectedImageBase64,
        setSelectedImageBase64
    ] = useState<string | undefined>();
    const [
        repeatNewPassword,
        setRepeatNewPassword
    ] = useState("");
    const [
        newPassword,
        setNewPassword
    ] = useState("");
    const [
        oldPassword,
        setOldPassword
    ] = useState("");
    const [
        editEmail,
        setEditEmail
    ] = useState(email || "");
    const [
        editPhone,
        setEditPhone
    ] = useState(phone || "");
    const [
        editAddress,
        setEditAddress
    ] = useState(address || "");

    useEffect(() => {
        if (isVisible) {
            setSelectedImageBase64(undefined);
            setRepeatNewPassword("");
            setNewPassword("");
            setOldPassword("");
            setEditEmail(email || "");
            setEditPhone(phone || "");
            setEditAddress(address || "");
        }
    }, [
        isVisible,
        address,
        email,
        phone
    ]);

    const handlePickImage = async () => {
        try {
            if (Platform.OS === "web") {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = (e: Event) => {
                    const target = e.target as HTMLInputElement;
                    const file = target.files?.[0];
                    if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                            const img = new Image();
                            img.onload = () => {
                                const canvas = document.createElement("canvas");
                                const MAX_WIDTH = 800;
                                const MAX_HEIGHT = 800;
                                let width = img.width;
                                let height = img.height;

                                if (width > height) {
                                    if (width > MAX_WIDTH) {
                                        height *= MAX_WIDTH / width;
                                        width = MAX_WIDTH;
                                    }
                                } else {
                                    if (height > MAX_HEIGHT) {
                                        width *= MAX_HEIGHT / height;
                                        height = MAX_HEIGHT;
                                    }
                                }

                                canvas.width = width;
                                canvas.height = height;
                                const ctx = canvas.getContext("2d");
                                ctx?.drawImage(img, 0, 0, width, height);

                                const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);
                                setSelectedImageBase64(compressedBase64);
                            };
                            if (event.target?.result) {
                                img.src = event.target.result as string;
                            }
                        };
                        reader.readAsDataURL(file);
                    }
                };
                input.click();
                return;
            }

            const result = await launchImageLibrary({
                includeBase64: true,
                mediaType: "photo",
                maxHeight: 800,
                maxWidth: 800,
                quality: 0.7
            });

            const assets = result?.assets;
            if (assets && assets.length > 0) {
                const asset = assets[0];
                if (asset && asset.base64) {
                    const ext = asset.type || "image/jpeg";
                    setSelectedImageBase64(`data:${ext};base64,${asset.base64}`);
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleSave = () => {
        if (newPassword && newPassword !== repeatNewPassword) {
            return;
        }

        onSave({
            profilePicturePath: selectedImageBase64,
            newPassword: newPassword || undefined,
            oldPassword: oldPassword || undefined,
            address: editAddress,
            email: editEmail,
            phone: editPhone
        });
    };

    const displayImage = selectedImageBase64 || profilePicturePath;

    return <Modal
        onOverlayPress={({
            closeAnimation
        }) => closeAnimation(onClose)}
        portalName="modal-system"
        id="edit-profile-modal"
        isActive={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.content.container.default,
                    width: Platform.OS === "web" ? "75%" : "90%",
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl
                }
            ]}
        >
            <Text
                style={{
                    marginBottom: spaces.spacingMd
                }}
                variant="titleLargeSize"
            >
                {localize("users-modal-edit-title")}
            </Text>
            <ScrollView
                style={stylesheet.scrollView}
            >
                <View
                    style={[
                        stylesheet.avatarContainer,
                        {
                            marginBottom: spaces.spacingXl * 1.5
                        }
                    ]}
                >
                    <Avatar
                        key={displayImage || "default-avatar"}
                        imageUrl={displayImage}
                        title={username}
                        size="xxLarge"
                    />
                    <TouchableOpacity
                        style={[
                            stylesheet.editIconContainer,
                            {
                                backgroundColor: colors.system.default.background,
                                borderColor: colors.content.border.subtle,
                                borderRadius: radiuses.full
                            }
                        ]}
                        onPress={handlePickImage}
                    >
                        <PencilIcon
                            color={colors.content.icon.default}
                            size={16}
                        />
                    </TouchableOpacity>
                </View>
                <View
                    style={[
                        stylesheet.inputsContainer,
                        {
                            marginBottom: spaces.spacingXl * 1.5,
                            gap: spaces.spacingXl
                        }
                    ]}
                >
                    <TextInput
                        placeholder={localize("superadmin-form-email-placeholder")}
                        title={localize("superadmin-form-email-title")}
                        onChangeText={setEditEmail}
                        spreadBehaviour="free"
                        value={editEmail}
                    />
                    <TextInput
                        placeholder={localize("superadmin-form-phone-placeholder")}
                        title={localize("superadmin-form-phone-title")}
                        onChangeText={setEditPhone}
                        spreadBehaviour="free"
                        value={editPhone}
                    />
                    <TextInput
                        placeholder={localize("superadmin-form-address-placeholder")}
                        title={localize("superadmin-form-address-title")}
                        onChangeText={setEditAddress}
                        spreadBehaviour="free"
                        value={editAddress}
                        multiline
                    />
                    <TextInput
                        placeholder={localize("users-modal-password-title")}
                        title={localize("users-modal-password-title")}
                        onChangeText={setOldPassword}
                        spreadBehaviour="free"
                        value={oldPassword}
                        secureTextEntry
                    />
                    <TextInput
                        placeholder={localize("reset-password-new-password-placeholder")}
                        title={localize("reset-password-new-password-placeholder")}
                        onChangeText={setNewPassword}
                        spreadBehaviour="free"
                        value={newPassword}
                        secureTextEntry
                    />
                    <TextInput
                        placeholder={localize("reset-password-new-password-confirm-placeholder")}
                        title={localize("reset-password-new-password-confirm-placeholder")}
                        onChangeText={setRepeatNewPassword}
                        value={repeatNewPassword}
                        spreadBehaviour="free"
                        secureTextEntry
                    />
                </View>
            </ScrollView>
            <Button
                title={localize("users-modal-save")}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                spreadBehaviour="stretch"
                onPress={handleSave}
            />
            <Button
                title={localize("users-modal-cancel")}
                spreadBehaviour="stretch"
                onPress={onClose}
                type="neutral"
            />
        </View>
    </Modal>;
};
export default EditProfileModal;
